import type { RootState } from 'src/redux/store';

import { useMemo, useEffect, useCallback } from 'react';

import { useAppDispatch, useAppSelector } from 'src/hook/hooks';
import { getUserRole } from 'src/pages/auth/hooks/permission-utils';
import { userApi } from 'src/pages/setting/components/users/slice/userApi';
import { usePermissionChecks } from 'src/pages/auth/hooks/usePermissionChecks';
import { usePermissionsSource } from 'src/pages/auth/hooks/usePermissionsSource';
import { mergeBootstrap, selectUserPresenceMap } from 'src/redux/slices/userPresenceSlice';

export type { UserPresenceSocketPayload } from 'src/types/user-presence';

/**
 * Reads tenant user presence from **global Redux** (updated by `UserPresenceSocketBridge` + REST here).
 * Triggers `GET /api/users/presence` when `enabled` and ids change (RTK dedupes). Requires `user.view`.
 */
export function useUserPresence(
  userIds: Array<string | undefined | null>,
  options?: { enabled?: boolean }
) {
  const enabled = options?.enabled ?? true;
  const dispatch = useAppDispatch();
  const presenceMap = useAppSelector((s: RootState) => selectUserPresenceMap(s));

  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);
  const permissions = usePermissionsSource(loginApiData);
  const userRole = useMemo(() => getUserRole(loginApiData), [loginApiData]);
  const { hasPermission } = usePermissionChecks(permissions, userRole);
  const canViewUserPresence = hasPermission('userprofile');

  const idsKey = [
    ...new Set(
      userIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    ),
  ]
    .sort()
    .join('|');

  const stableIds = useMemo(() => (idsKey ? idsKey.split('|') : []), [idsKey]);

  useEffect(() => {
    if (!enabled || !canViewUserPresence || stableIds.length === 0) {
      return undefined;
    }

    let cancelled = false;
    const req = dispatch(userApi.endpoints.getUsersPresence.initiate(stableIds));
    req
      .unwrap()
      .then((data) => {
        if (!cancelled && data) {
          dispatch(mergeBootstrap(data));
        }
      })
      .catch(() => {
        /* 403 / network — leave map unchanged */
      });

    return () => {
      cancelled = true;
      req.unsubscribe?.();
    };
  }, [enabled, canViewUserPresence, idsKey, dispatch]);

  const isUserOnline = useCallback(
    (userId: string | undefined | null) =>
      !!(userId && typeof userId === 'string' && presenceMap[userId]),
    [presenceMap]
  );

  const refetch = useCallback(() => {
    if (!canViewUserPresence || stableIds.length === 0) return;
    dispatch(userApi.endpoints.getUsersPresence.initiate(stableIds, { forceRefetch: true }))
      .unwrap()
      .then((data) => {
        if (data) dispatch(mergeBootstrap(data));
      })
      .catch(() => {});
  }, [canViewUserPresence, dispatch, stableIds]);

  return {
    presence: presenceMap,
    isUserOnline,
    canViewUserPresence,
    refetch,
    isFetching: false,
    isError: false,
  };
}

/**
 * Hydrates current user in the global presence map and exposes permission + lookup helpers
 * for the header account avatar dot.
 */
export function useCurrentUserPresenceShine(currentUserId: string | undefined) {
  const { isUserOnline, canViewUserPresence } = useUserPresence(
    currentUserId ? [currentUserId] : [],
    { enabled: !!currentUserId }
  );

  return { canViewUserPresence, isUserOnline };
}
